<template>
  <div
    class="winLoseContainer"
    :class="{
      winLoseContainerMobile: $device.isMobile
    }"
    @click="playVideo()"
  >
    <div class="header">
      <p
        class="title"
        :class="{
          'ru-locale': isUserNameRuLocale
        }"
      >
        {{ name }}<span>,</span>
      </p>
    </div>
    <div class="body">
      <p class="result" :class="data?.isWinner ? 'greenTitle' : 'redTitle'">
        {{ $t(data?.isWinner ? 'coinPusherWin' : 'coinPusherLose') }}
      </p>
      <video
        ref="videoRef"
        class="sack"
        :style="{
          height: '100%',
          width: '100%',
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: -1
        }"
        preload="auto"
        autoplay
        loop
        muted
        playsinline
        webkit-playsinline
        x5-playsinline
        x-webkit-airplay="allow"
        :src="
          data?.isWinner ? `/flipCoinSacks/${getTokenName(props?.tokenAddress || '')}.mp4` : `/flipCoinSacks/trash.mp4`
        "
        @loadeddata="onVideoLoaded"
        @canplay="onVideoLoaded"
        @ended="onVideoEnded"
      />

      <div class="bottom-container" :class="{ 'ru-locale': $i18n.locale === 'ru' }">
        <div class="youGet">
          <span class="youGetLabel">{{ $t('buypackYouGet') }}:</span>

          <div v-if="data?.isWinner" class="tokenRewards">
            <img
              class="icon-image"
              :src="
                tokens[
                  tokenMetaData(blockchain.contracts, props?.tokenAddress || '').name.contract as Tokens
                ].toString()
              "
              width="32"
              loading="lazy"
              alt="Token icon"
            />
            <span>
              {{ data?.winAmount ? addThousandSeparators(data.winAmount) : 0 }}
            </span>
          </div>

          <div class="seasonPointsRewards">
            <img class="icon-image" :src="tokens.seasonPoints.toString()" width="32" loading="lazy" alt="Token icon" />
            <span>
              {{ data?.seasonPoints ? addThousandSeparators(data.seasonPoints) : 0 }}
            </span>
          </div>
        </div>

        <div class="buttons">
          <button class="btn-outline" :class="{ 'ru-locale': $i18n.locale === 'ru' }" @click="$emit('go-to-stats')">
            {{ $t('flipCoinViewStats') }}
          </button>
          <TavernFlipCoinBigButton class="getOutBtn" :label="actionBtnLabel" @click="$emit('get-out')" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import tokens from '~/public/img/tokens';
import { type Tokens } from '~/utils/constants';
import type { PusherConfigResponse, PusherGameResultResponse } from '~/types/Pusher';
import { addThousandSeparators } from '~/utils/formatters';

const { blockchain } = useEnvs();
const { name } = useUserName();
const { t } = useI18n();

const { data } = useNuxtData<PusherGameResultResponse>('pusherGameResult');
const { data: pusherConfig } = useNuxtData<PusherConfigResponse>('pusherConfig');
const actionBtnLabel = computed(() => {
  if (pusherConfig.value?.remainingBetLimit === 0) {
    return 'OK';
  }
  return t(data.value?.isWinner ? 'coinPusherPlayMore' : 'coinPusherTryAgain');
});
defineEmits(['go-to-stats', 'get-out']);

const props = defineProps<{
  tokenAddress: string;
}>();

const getTokenName = (tokenAddress: string) => {
  return tokenMetaData(blockchain.contracts, tokenAddress).name.contract;
};

const isUserNameRuLocale = computed(() => name.value && /^[А-Яа-я]/.test(name.value));

const videoRef = ref<HTMLVideoElement | null>(null);
const videoPlaying = ref(false);
let intersectionObserver: IntersectionObserver | null = null;
let continuousRetryInterval: ReturnType<typeof setInterval> | null = null;

const forcePlayVideo = async () => {
  const video = videoRef.value;
  if (!video) return false;

  try {
    video.muted = true;
    video.volume = 0;
    video.defaultMuted = true;

    const playPromise = video.play();

    if (playPromise !== undefined) {
      await playPromise;
      videoPlaying.value = true;
      return true;
    }
  } catch (error) {
    console.log('Video play attempt failed, will retry');
    return false;
  }
  return false;
};

const playVideo = async () => {
  if (videoPlaying.value) return;

  await forcePlayVideo();
};

const onVideoLoaded = () => {
  forcePlayVideo();
};

const onVideoEnded = () => {
  videoPlaying.value = false;
  setTimeout(() => {
    forcePlayVideo();
  }, 50);
};

onMounted(() => {
  const video = videoRef.value;
  if (!video) return;

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !videoPlaying.value) {
          forcePlayVideo();
        }
      });
    },
    { threshold: 0.1 }
  );

  intersectionObserver.observe(video);

  nextTick(() => {
    playVideo();
  });

  const maxTime = 5000;
  const startTime = Date.now();

  continuousRetryInterval = setInterval(() => {
    if (videoPlaying.value || Date.now() - startTime > maxTime) {
      if (continuousRetryInterval) {
        clearInterval(continuousRetryInterval);
        continuousRetryInterval = null;
      }
      return;
    }
    forcePlayVideo();
  }, 300);
});

onBeforeUnmount(() => {
  if (intersectionObserver) {
    intersectionObserver.disconnect();
  }
  if (continuousRetryInterval) {
    clearInterval(continuousRetryInterval);
    continuousRetryInterval = null;
  }
});

watch(
  () => data.value?.isWinner,
  () => {
    videoPlaying.value = false;
    nextTick(() => {
      const video = videoRef.value;
      if (video) {
        video.load();
        setTimeout(() => {
          playVideo();
        }, 50);
      }
    });
  }
);
</script>

<style lang="scss" scoped>
.winLoseContainerMobile {
  .buttons {
    button {
      flex-basis: 50%;
      width: fit-content;
    }
  }
}

.winLoseContainer {
  .header {
    margin: 10px 20px;
    position: relative;

    .title {
      font-size: 24px;
      line-height: 1;
      color: #fff;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 600;
      text-align: center;
    }
  }

  .youGet {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #fff;

    .youGetLabel {
      font-weight: 500;
      font-size: 20px;
    }

    span {
      font-weight: 700;
      font-size: 24px;
    }

    img {
      margin-right: 2px;
    }
  }

  .greenTitle {
    font-family: Eczar;
    font-weight: 600;
    font-size: 24px;
    line-height: 1.2;
    color: #12f83a;
  }

  .redTitle {
    font-family: Eczar;
    font-weight: 600;
    font-size: 24px;
    line-height: 1.2;
    color: rgba(255, 32, 36, 1);
  }

  .body {
    text-align: center;
    font-size: 20px;
  }

  .result {
    font-weight: 600;
    font-size: 24px;
    line-height: 1;
  }

  .buttons {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 12px;
    margin-bottom: 0px;
    gap: 10px;
    width: 100%;
    padding: 0 30px;

    button {
      height: 52px;
      font-size: 18px;
      white-space: nowrap;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .getOutBtn {
      margin: 0px;
      line-height: 24px;
      padding: 0 24px;
    }

    .btn-outline.ru-locale {
      min-width: 150px;
    }
  }

  .seasonPointsRewards {
    display: flex;
    align-items: center;
    justify-content: center;

    & > span {
      font-weight: 700;
      font-size: 24px;
    }
  }

  .tokenRewards {
    display: flex;
    align-items: center;

    span {
      font-weight: 500;
    }
  }

  .ru-locale {
    font-family: 'PT Serif', sans-serif;
  }

  .bottom-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    bottom: 30px;
    left: 50%;
    width: 100%;
    transform: translateX(-50%);
  }
}
</style>
